import { call, takeLatest } from 'redux-saga/effects';
import * as api from '@api/qcPatientVisits';
import { actions } from './QCPatientVisitsDates.slice';
import {
  QCPatientVisitsDatesFetchAction,
  QCPatientVisitUpdateOrderAction,
  QCPatientVisitLockAction,
  QCPatientVisitUnlockAction,
} from './QCPatientVisitsDates.types';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';
import { actions as qcTaskDefinitionActions } from '../QCTaskDefinition/QCTaskDefinition.slice';

export function* fetchQCPatientVisitsDatesWorker(action: QCPatientVisitsDatesFetchAction) {
  try {
    const response: Resp<typeof api.fetchQCPatientVisitsDates> = yield call(
      api.fetchQCPatientVisitsDates,
      action.payload.clinicalTrialId,
      action.payload.patientId
    );
    yield dispatch(actions.fetchQCPatientVisitsDatesSuccess(response.data.visits));
  } catch (error) {
    yield dispatch(actions.fetchQCPatientVisitsDatesFailure(error as Error));
  }
}

export function* updateVisitOrderWorker(
  action: QCPatientVisitUpdateOrderAction
) {
  try {
    const {
      clinicalTrialId,
      patientId,
      visitId,
      previousVisitId,
      previousVisitDefinitionId,
    } = action.payload;

    yield call(
      api.moveQCPatientVisitOrder,
      clinicalTrialId,
      patientId,
      visitId,
      { previousVisitId, previousVisitDefinitionId }
    );

    yield dispatch(actions.lockVisit({ clinicalTrialId, patientId, visitId }));

    yield call(runToast, 'success', 'Visit order updated');
  } catch (error) {
    yield dispatch(actions.fetchQCPatientVisitsDatesFailure(error as Error));
    yield call(runToast, 'error', 'Failed to update visit order');
  }
}

export function* lockVisitWorker(action: QCPatientVisitLockAction) {
  try {
    const { clinicalTrialId, patientId, visitId } = action.payload;

    yield call(api.lockQCPatientVisit, clinicalTrialId, patientId, visitId);

    const response: Resp<typeof api.fetchQCPatientVisitsDates> = yield call(
      api.fetchQCPatientVisitsDates,
      clinicalTrialId,
      patientId
    );

    yield dispatch(
      actions.fetchQCPatientVisitsDatesSuccess(response.data.visits)
    );

    yield dispatch(
      qcTaskDefinitionActions.fetchQCTaskDefinitions({
        clinicalTrialId,
        patientId,
        visitId,
      })
    );
  } catch (error) {
    yield dispatch(actions.fetchQCPatientVisitsDatesFailure(error as Error));
    yield call(runToast, 'error', 'Failed to lock visit');
  }
}

export function* unlockVisitWorker(action: QCPatientVisitUnlockAction) {
  try {
    const { clinicalTrialId, patientId, visitId, comment } = action.payload;

    yield call(
      api.unlockQCPatientVisit,
      clinicalTrialId,
      patientId,
      visitId,
      comment
    );
    yield dispatch(qcTaskDefinitionActions.fetchQCTaskDefinitionsSuccess([]));

    const response: Resp<typeof api.fetchQCPatientVisitsDates> = yield call(
      api.fetchQCPatientVisitsDates,
      clinicalTrialId,
      patientId
    );

    yield dispatch(
      actions.fetchQCPatientVisitsDatesSuccess(response.data.visits)
    );

    yield call(runToast, 'success', 'Visit unlocked successfully');
  } catch (error) {
    yield dispatch(actions.fetchQCPatientVisitsDatesFailure(error as Error));
    yield call(runToast, 'error', 'Failed to unlock visit');
  }
}

export function* QCPatientVisitsDatesSaga() {
  yield takeLatest(
    actions.fetchQCPatientVisitsDates,
    fetchQCPatientVisitsDatesWorker
  );
  yield takeLatest(actions.updateVisitOrder, updateVisitOrderWorker);
  yield takeLatest(actions.lockVisit, lockVisitWorker);
  yield takeLatest(actions.unlockVisit, unlockVisitWorker);
}
